import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import { FiDownload, FiX, FiCheckCircle, FiCopy } from 'react-icons/fi';
import classNames from 'classnames';
import * as QRCode from 'qrcode';

interface Props {
  open: boolean;
  onClose: () => void;
  userId: number;
  userName: string;
  chartNumber: string;
}

const AdminInvitationQRDialog: React.FC<Props> = ({ open, onClose, userId, userName, chartNumber }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [tokenData, setTokenData] = useState<{ token: string; link: string } | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open && userId) {
      generateToken();
    } else {
      setTokenData(null);
      setQrCodeUrl(null);
      setCopied(false);
    }
  }, [open, userId]);

  const generateToken = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/invitation/generate?user_id=${userId}`);
      const data = res.data;
      setTokenData({ token: data.token, link: data.link });

      const fullUrl = `${window.location.origin}${data.link}`;

      // Create a canvas for processing
      const canvas = document.createElement('canvas');
      const CARD_W = 600;
      const CARD_H = 800; // 3:4 ratio
      canvas.width = CARD_W;
      canvas.height = CARD_H;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('canvas-2d-unavailable');

      // 1. Background
      const g = ctx.createLinearGradient(0, 0, CARD_W, CARD_H);
      g.addColorStop(0, '#ffffff');
      g.addColorStop(1, '#f9fafb');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, CARD_W, CARD_H);

      // 2. Load Photos
      const mainLogoSrc = '/images/Melodislogo.png';
      const centerLogoSrc = '/logo192.png'; // Fallback to a safer filename

      const loadImage = (src: string) => new Promise<HTMLImageElement | null>((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => {
          console.error(`Failed to load image for QR Card: ${src}`);
          resolve(null);
        };
        img.src = src;
      });

      const [mainLogo, centerLogo] = await Promise.all([
        loadImage(mainLogoSrc),
        loadImage(centerLogoSrc)
      ]);

      // 3. Draw Header
      if (mainLogo) {
        const LOGO_H = 100;
        const ar = (mainLogo.width || 1) / (mainLogo.height || 1);
        const logoW = LOGO_H * ar;
        ctx.drawImage(mainLogo, (CARD_W - logoW) / 2, 60, logoW, LOGO_H);
      }
      
      // Removed "Melodis Clinic" text as requested

      ctx.textBaseline = 'top';
      ctx.font = 'bold 36px sans-serif';
      ctx.fillStyle = '#1e293b';
      const subtitle = t('admin.qr_login_title') || 'WeChat ログイン';
      const subtitleW = ctx.measureText(subtitle).width;
      ctx.fillText(subtitle, (CARD_W - subtitleW) / 2, 180);

      // 4. Generate & Draw QR
      const qrCanvas = document.createElement('canvas');
      const qrSize = 420;
      await (QRCode as any).toCanvas(qrCanvas, fullUrl, {
        width: qrSize,
        margin: 1,
        errorCorrectionLevel: 'H',
      });

      const qrX = (CARD_W - qrSize) / 2;
      const qrY = 240;
      ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

      // 5. Draw QR Center Logo
      if (centerLogo) {
        const logoSize = qrSize * 0.2;
        const lx = qrX + (qrSize - logoSize) / 2;
        const ly = qrY + (qrSize - logoSize) / 2;

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(lx - 4, ly - 4, logoSize + 8, logoSize + 8);
        ctx.drawImage(centerLogo, lx, ly, logoSize, logoSize);
      }

      // 6. Footer Text
      ctx.font = 'bold 28px sans-serif';
      ctx.fillStyle = '#0f172a';
      // Use chartNumber instead of ID
      const targetTxt = `${userName} (${t('doctor.chart_number') || 'カルテ番号'}: ${chartNumber})`;
      const targetW = ctx.measureText(targetTxt).width;
      ctx.fillText(targetTxt, (CARD_W - targetW) / 2, 680);

      ctx.font = '22px sans-serif';
      ctx.fillStyle = '#64748b';
      const hint1 = t('admin.qr_hint_1') || 'WeChat でスキャンしてログイン';
      const hint1W = ctx.measureText(hint1).width;
      ctx.fillText(hint1, (CARD_W - hint1W) / 2, 725);

      const hint2 = t('admin.qr_hint_2') || '扫描二维码，完成微信绑定并登录';
      const hint2W = ctx.measureText(hint2).width;
      ctx.fillText(hint2, (CARD_W - hint2W) / 2, 755);

      setQrCodeUrl(canvas.toDataURL('image/png'));
    } catch (err) {
      console.error('Error generating invitation token:', err);
      toast.error(t('admin.invitation_failed') || '生成邀请令牌失败');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!qrCodeUrl) return;
    const a = document.createElement('a');
    a.href = qrCodeUrl;
    a.download = `login_qr_${userName}_${chartNumber}.png`;
    a.click();
  };

  const handleCopyLink = () => {
    if (!tokenData) return;
    const fullUrl = `${window.location.origin}${tokenData.link}`;
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success(t('admin.link_copied') || '链接已复制到剪贴板');
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div 
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between border-b bg-gray-50">
          <h3 className="text-lg font-bold text-gray-800">
            📲 {t('admin.generate_qr_login') || '生成登录二维码'}
          </h3>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
          >
            <FiX className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8 flex flex-col items-center">
          <div className="mb-4 text-center">
            <p className="text-sm text-gray-500">{t('admin.target_user_carte') || '対象ユーザー (カルテ番号)'}:</p>
            <p className="font-semibold text-gray-900">{userName} ({chartNumber})</p>
          </div>

          {/* Aspect Ratio Fix: w-64 is 16rem, so h should be 16 * 4/3 = 21.33rem */}
          <div className="relative w-64 h-[21.33rem] bg-gray-100 rounded-2xl flex items-center justify-center border-2 border-dashed border-gray-300 overflow-hidden">
            {loading ? (
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 border-4 border-coral border-t-transparent rounded-full animate-spin mb-3"></div>
                <p className="text-xs text-gray-500">{t('admin.generating') || '生成中...'}</p>
              </div>
            ) : qrCodeUrl ? (
              <img src={qrCodeUrl} alt="Login QR Code" className="w-full h-full object-cover" />
            ) : (
              <p className="text-xs text-red-500">Failed to load QR</p>
            )}
          </div>

          {tokenData && (
            <div className="mt-6 w-full space-y-3">
              <div className="w-full break-all bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-center text-gray-600 font-mono select-all">
                {`${window.location.origin}${tokenData.link}`}
              </div>
              <button
                onClick={handleDownload}
                className="w-full flex items-center justify-center gap-2 bg-coral hover:opacity-90 text-white font-semibold py-3 rounded-xl shadow-lg transition-all active:scale-[0.98]"
              >
                <FiDownload className="w-5 h-5" />
                {t('admin.download_qr') || '下载二维码'}
              </button>
              
              <button
                onClick={handleCopyLink}
                className={classNames(
                  "w-full flex items-center justify-center gap-2 font-medium py-3 rounded-xl border-2 transition-all active:scale-[0.98]",
                  copied 
                    ? "bg-green-50 border-green-500 text-green-700" 
                    : "bg-white border-gray-200 hover:bg-gray-50 text-gray-700"
                )}
              >
                {copied ? <FiCheckCircle className="w-5 h-5" /> : <FiCopy className="w-5 h-5" />}
                {copied ? (t('admin.copied') || '已复制') : (t('admin.copy_link') || '复制链接')}
              </button>
            </div>
          )}

          <p className="mt-6 text-[11px] text-gray-400 text-center leading-relaxed whitespace-pre-line">
            {t('admin.qr_hint') || '※ このQRコードは初回スキャン時にデバイスと紐付けられます。\nスキャン後は直接ログインが可能になり、再発行の必要はありません。'}
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {t('common.close') || '閉じる'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminInvitationQRDialog;
